import { useEffect, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, Circle, useMapEvents } from "react-leaflet";
import { useLocation } from "react-router-dom";
import L from "leaflet";
import { apiGet } from "../api/api";

// ===============================
// ÍCONE DO UTILIZADOR
// ===============================
const userIcon = L.icon({
  iconUrl: "https://cdn-icons-png.flaticon.com/512/64/64113.png",
  iconSize: [34, 34],
  iconAnchor: [17, 34],
});

// ===============================
// ÍCONES BASE POR OPERADOR
// ===============================
const icons = {
  DREAMMEDIA: { iconUrl: "/icons/marker-orange.png" },
  JCDECAUX: { iconUrl: "/icons/marker-blue.png" },
  MOP: { iconUrl: "/icons/marker-red.png" },
  default: { iconUrl: "/icons/marker-grey.png" },
};

function getBaseIconUrl(operatorName) {
  if (!operatorName) return icons.default.iconUrl;

  const name = operatorName.toUpperCase();
  if (name.includes("DREAM")) return icons.DREAMMEDIA.iconUrl;
  if (name.includes("JCDECAUX")) return icons.JCDECAUX.iconUrl;
  if (name.includes("MOP")) return icons.MOP.iconUrl;

  return icons.default.iconUrl;
}

// ===============================
// ÍCONE COM BADGE SEMANAL
// ===============================
function getIconComEstado(item, estadoSemanal) {
  const baseIconUrl = getBaseIconUrl(item.operador?.nome);
  const auditado = estadoSemanal[item.id];

  let badge = "";
  let badgeColor = "";

  if (auditado === true) {
    badge = "✓";
    badgeColor = "#2e7d32";
  } else if (auditado === false) {
    badge = "!";
    badgeColor = "#c62828";
  }

  return L.divIcon({
    className: "",
    html: `
      <div style="position: relative; width: 34px; height: 34px;">
        <img src="${baseIconUrl}" style="width: 34px; height: 34px;" />
        ${
          badge
            ? `<div style="
                position: absolute;
                top: -6px;
                right: -6px;
                width: 18px;
                height: 18px;
                background: ${badgeColor};
                color: white;
                border-radius: 50%;
                font-size: 12px;
                font-weight: bold;
                display: flex;
                align-items: center;
                justify-content: center;
                border: 2px solid white;
              ">
                ${badge}
              </div>`
            : ""
        }
      </div>
    `,
    iconSize: [34, 34],
    iconAnchor: [17, 34],
  });
}

// ===============================
// COMPONENTE PARA CAPTURAR BOUNDS
// ===============================
function MapBounds({ setBounds }) {
  useMapEvents({
    moveend: (e) => {
      const map = e.target;
      const newBounds = map.getBounds();
      setBounds(newBounds);

      const center = map.getCenter();
      if (typeof window !== "undefined") {
        window.sessionStorage.setItem(
          "inventarioMapa_center",
          JSON.stringify({ lat: center.lat, lng: center.lng })
        );
      }
    },
  });
  return null;
}

export default function InventarioMapa() {
  const location = useLocation();

  const [inventario, setInventario] = useState([]);
  const [estadoSemanal, setEstadoSemanal] = useState({});
 // por exemplo:
  const DEFAULT_POSITION = { lat: 41.16632200721439, lon: -8.602613532460188 };

  // tentar recuperar centro anterior do sessionStorage
  let initialPosition = DEFAULT_POSITION;
  if (typeof window !== "undefined") {
    const storedCenter = window.sessionStorage.getItem("inventarioMapa_center");
    if (storedCenter) {
      try {
        const c = JSON.parse(storedCenter);
        if (typeof c.lat === "number" && typeof c.lng === "number") {
          initialPosition = { lat: c.lat, lon: c.lng };
        }
      } catch (e) {
        console.error("Erro a ler center do sessionStorage", e);
      }
    }
  }

  const [position, setPosition] = useState(initialPosition);
  const [gpsLoaded, setGpsLoaded] = useState(false); // opcional


  // 🔍 NOVO
  const [search, setSearch] = useState("");
  const [bounds, setBounds] = useState(null);

  // ======================================================
  // 1️⃣ INVENTÁRIO + ESTADO SEMANAL
  // ======================================================
  useEffect(() => {
    async function loadTudo() {
      const inv = await apiGet("/inventario/");
      setInventario(inv);

      const data = await apiGet("/campanhas/auditoria-semanal");
      const mapa = {};
      data.inventarios.forEach((i) => {
        mapa[i.inventario_id] = i.auditado;
      });
      setEstadoSemanal(mapa);
    }

    loadTudo();
  }, [location.search]);

  // ======================================================
  // 2️⃣ GPS
  // ======================================================
  useEffect(() => {
    if (!navigator.geolocation) {
      console.warn("Geolocalização não suportada");
      setGpsLoaded(true);
      return;
    }

    const watchId = navigator.geolocation.watchPosition(
      (pos) => {
        setPosition({
          lat: pos.coords.latitude,
          lon: pos.coords.longitude,
        });
        setGpsLoaded(true);
      },
      (err) => {
        console.error("Erro GPS:", err);
        setGpsLoaded(true);
        // se falhar, ficas com a posição que veio do sessionStorage ou default
      },
      {
        enableHighAccuracy: false,
        maximumAge: 600000, // aceita até 10 min
        timeout: 20000,
      }
    );

    return () => navigator.geolocation.clearWatch(watchId);
  }, []);


  // ======================================================
  // 🔍 FILTRO POR BOUNDS + TEXTO
  // ======================================================
  const inventarioFiltrado = inventario.filter((item) => {
    if (bounds) {
      const dentro =
        item.latitude >= bounds.getSouth() &&
        item.latitude <= bounds.getNorth() &&
        item.longitude >= bounds.getWest() &&
        item.longitude <= bounds.getEast();

      if (!dentro) return false;
    }

    if (!search) return true;

    const s = search.toLowerCase();

    return (
      String(item.id).includes(s) ||
      item.operador?.nome?.toLowerCase().includes(s) ||
      item.tipo_localizacao?.toLowerCase().includes(s) ||
      item.concelho?.toLowerCase().includes(s) ||
      item.freguesia?.toLowerCase().includes(s)
    );
  });

  return (
    <div style={{ height: "100vh", width: "100%" }}>
      {/* 🔍 BARRA DE PESQUISA */}
      <div
        style={{
          position: "absolute",
          bottom: 20,
          left: 10,
          zIndex: 1000,
          background: "white",
          padding: 8,
          borderRadius: 6,
          boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
          width: 260,
        }}
      >
        <input
          placeholder="🔍 ID, operador, concelho..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            width: "100%",
            padding: 8,
            fontSize: 14,
          }}
        />
        <div style={{ fontSize: 12, marginTop: 4, color: "#555" }}>
          {inventarioFiltrado.length} equipamentos visíveis
        </div>
      </div>

      <MapContainer
        center={[position.lat, position.lon]}
        zoom={17}
        style={{ height: "100%", width: "100%" }}
      >
        <MapBounds setBounds={setBounds} />

        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />

        <Marker position={[position.lat, position.lon]} icon={userIcon}>
          <Popup>Você está aqui 📍</Popup>
        </Marker>

        <Circle
          center={[position.lat, position.lon]}
          radius={50}
          pathOptions={{
            color: "blue",
            fillColor: "blue",
            fillOpacity: 0.15,
          }}
        />

        {inventarioFiltrado.map((item) => (
          <Marker
            key={item.id}
            position={[item.latitude, item.longitude]}
            icon={getIconComEstado(item, estadoSemanal)}
          >
            <Popup>
              <strong>ID {item.id}</strong>
              <br />
              <strong>Tipo Localização:</strong> {item.tipo_localizacao}
              <br />
              <strong>Operador:</strong> {item.operador?.nome}
              <br />

              {estadoSemanal[item.id] === true && (
                <div style={{ color: "green", fontWeight: "bold", marginTop: 5 }}>
                  ✅ Auditado esta semana
                </div>
              )}

              {estadoSemanal[item.id] === false && (
                <div style={{ color: "red", fontWeight: "bold", marginTop: 5 }}>
                  ⚠️ Não auditado esta semana
                </div>
              )}

              <button
                style={{ marginTop: 8, padding: "5px 10px", cursor: "pointer" }}
                onClick={() =>
                  (window.location.href = `/inventario/${item.id}`)
                }
              >
                🔧 Ver detalhes
              </button>

              <br />

              <button
                style={{
                  marginTop: 5,
                  padding: "5px 10px",
                  cursor: "pointer",
                  background: "#2e7d32",
                  color: "white",
                  border: "none",
                  borderRadius: 4,
                }}
                onClick={() =>
                  (window.location.href = `/campo/inventario/${item.id}?refresh=1`)
                }
              >
                📸 Inserir campanha
              </button>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
}
